
import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

const Modal = ({ isOpen, onClose, title, children, size = 'md', resizable = false }) => {
    // Size mapping for modal widths
    const sizeClasses = {
        'sm': 'max-w-md',
        'md': 'max-w-lg',
        'lg': 'max-w-2xl',
        'xl': 'max-w-4xl',
        '2xl': 'max-w-6xl',
        'full': 'max-w-full mx-4'
    };
    
    const widthClass = sizeClasses[size] || sizeClasses['md'];
    const modalContentRef = useRef(null);
    const modalRef = useRef(null);
    const [sidebarWidth, setSidebarWidth] = useState(256); // Default to expanded width (w-64 = 256px)
    
    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        // Cleanup on unmount
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle Escape key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        
        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            return () => {
                document.removeEventListener('keydown', handleEscape);
            };
        }
    }, [isOpen, onClose]);

    // Ensure modal is properly closed if isOpen becomes false
    useEffect(() => {
        if (!isOpen && modalRef.current) {
            // Force cleanup if modal is supposed to be closed
            document.body.style.overflow = '';
        }
    }, [isOpen]);

    // Detect sidebar width dynamically
    useEffect(() => {
        if (isOpen) {
            const detectSidebarWidth = () => {
                // Find the sidebar element
                const sidebar = document.querySelector('aside[class*="w-"]');
                if (sidebar) {
                    const computedStyle = window.getComputedStyle(sidebar);
                    const width = parseInt(computedStyle.width, 10);
                    if (!isNaN(width)) {
                        setSidebarWidth(width);
                    }
                }
            };

            // Detect on mount and when sidebar might change
            detectSidebarWidth();
            
            // Set up observer for sidebar width changes
            const observer = new MutationObserver(detectSidebarWidth);
            const sidebar = document.querySelector('aside[class*="w-"]');
            if (sidebar) {
                observer.observe(sidebar, {
                    attributes: true,
                    attributeFilter: ['class']
                });
            }

            // Also check on window resize
            window.addEventListener('resize', detectSidebarWidth);

            return () => {
                observer.disconnect();
                window.removeEventListener('resize', detectSidebarWidth);
            };
        }
    }, [isOpen]);

    if (!isOpen) return null;
    
    const handleBackdropClick = (e) => {
        // Only close if clicking the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
            onClose();
        }
    };
    
    return (
        <div 
            ref={modalRef}
            className="fixed bg-black bg-opacity-50 z-[150] flex items-center p-4"
            style={{ 
                pointerEvents: isOpen ? 'auto' : 'none',
                top: 0,
                bottom: 0,
                left: `${sidebarWidth}px`, // Position to the right of sidebar
                right: 0,
                width: `calc(100% - ${sidebarWidth}px)` // Remaining width after sidebar
            }}
            onClick={handleBackdropClick}
            onMouseDown={(e) => {
                // Prevent drag events from propagating when clicking modal
                if (e.target === e.currentTarget) {
                    e.stopPropagation();
                }
            }}
        >
            <div 
                ref={modalContentRef}
                className={`bg-white rounded-lg shadow-2xl w-full ${widthClass} max-h-full overflow-y-auto mx-auto ${resizable ? 'resize overflow-auto' : ''}`}
                style={resizable ? {
                    resize: 'both',
                    minWidth: '400px',
                    minHeight: '300px',
                    maxWidth: `calc(100vw - ${sidebarWidth + 100}px)`, // Account for sidebar + padding
                    maxHeight: '95vh'
                } : {
                    maxWidth: `calc(100vw - ${sidebarWidth + 100}px)` // Account for sidebar + padding
                }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white z-10">
                    <h3 className="text-lg font-bold">{title}</h3>
                    <button 
                        onClick={onClose} 
                        className="p-1 rounded-full text-gray-500 hover:bg-gray-200"
                        aria-label="Close modal"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

export default Modal;
